/**
 * SNS Post Utils
 *
 * Post-level SNS glue.
 * Utility helpers (optional in V2).
 */

import { Roles } from "../../constants/roles";
import { SNSLinksUtils } from "../links/sns-links-utils";
import {
    extractInstagramPermalinkFromOfficialEmbedHtml,
    normalizeInstagramPermalinkForEmbed,
} from "../extractor/instagram-embed-extractor";
import { convertInstagramOfficialEmbedHtmlToMinimalEmbedHtml } from "../extractor/instagram-embed-minifier";
import {
    extractTikTokVideoUrlFromOfficialEmbedHtml,
    normalizeTikTokVideoUrl,
} from "../extractor/tiktok-embed-extractor";
import { extractFacebookPostUrlFromEmbedHtml } from "../extractor/facebook-embed-extractor";

export class SNSPostUtils {
    private static getSnsLinkInputs(snsLinksList: HTMLElement): Array<HTMLInputElement | HTMLTextAreaElement> {
        const queue = Array.from(snsLinksList.children);
        const inputs: Array<HTMLInputElement | HTMLTextAreaElement> = [];

        while (queue.length > 0) {
            const current = queue.shift();
            if (!(current instanceof HTMLElement)) {
                continue;
            }

            if (
                (current instanceof HTMLInputElement || current instanceof HTMLTextAreaElement) &&
                current.getAttribute("data-role") === Roles.SNS_LINK_INPUT
            ) {
                inputs.push(current);
            }

            queue.push(...Array.from(current.children));
        }

        return inputs;
    }

    /**
     * Collect normalized SNS URLs from sns_links[] inputs.
     */
    public static getNormalizedSnsUrlsFromList(snsLinksList: HTMLElement | null | undefined): string[] {
        if (!snsLinksList) return [];

        const inputs = SNSPostUtils.getSnsLinkInputs(snsLinksList);

        const expanded: string[] = [];

        Array.from(inputs).forEach((inputEl) => {
            const value = String(inputEl.value || "").trim();
            if (!value) return;

            // Facebook plugin embed iframe HTML pasted into sns_links
            if (
                (value.includes("facebook.com/plugins/") ||
                    value.includes("<iframe") ||
                    value.includes("&lt;iframe")) &&
                (value.includes("href=") || value.includes("?href=") || value.includes("&href="))
            ) {
                const extracted = extractFacebookPostUrlFromEmbedHtml(value);
                if (extracted) {
                    inputEl.value = extracted;
                    expanded.push(extracted);
                    return;
                }
            }

            if (
                value.includes("instagram-media") ||
                value.includes("data-instgrm-permalink") ||
                value.includes("<blockquote")
            ) {
                // First, run through the shared converter (official -> minimal HTML)
                // then extract permalink from the minimal HTML.
                const minimal = convertInstagramOfficialEmbedHtmlToMinimalEmbedHtml(value);

                const extracted = extractInstagramPermalinkFromOfficialEmbedHtml(minimal || value);
                const normalized = extracted ? normalizeInstagramPermalinkForEmbed(extracted) : null;
                const out = normalized || extracted;

                if (out && typeof out === "string") {
                    // URL-only storage: replace huge embed HTML with permalink.
                    inputEl.value = out;
                    expanded.push(out);
                    return;
                }
            }

            // TikTok official embed (blockquote.tiktok-embed) pasted into sns_links
            if (value.includes("tiktok-embed") || value.includes("data-video-id") || value.includes("tiktok.com")) {
                const extracted = extractTikTokVideoUrlFromOfficialEmbedHtml(value) || value;
                const normalized = extracted ? normalizeTikTokVideoUrl(extracted) : null;
                const out = normalized || extracted;

                if (out && typeof out === "string" && out.includes("tiktok.com")) {
                    inputEl.value = out;
                    expanded.push(out);
                    return;
                }
            }

            expanded.push(value);
        });

        return SNSLinksUtils.normalizeSnsLinks(expanded);
    }
}
